import React, { useCallback, useEffect } from "react";

import { styled } from "@mui/material/styles";

import { Container, Typography } from "@mui/material";
import DashboardList from "../../components/dashboardList/DashboardList";
import { useViewport } from "../../helpers";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { fetchMyExpariences } from "../../reducers/expariences/expariencesThunks";
import { fetchMyDesigns } from "../../reducers/myDesigns/myDesignsThunks";
import { myExpariencesService } from "../../services";

const PREFIX = "UserDashboard";

const classes = {
  generalPadding: `${PREFIX}-generalPadding`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.generalPadding}`]: {
    paddingLeft: "16px",
  },
}));

const UserDashboard = () => {
  const dispatch = useDispatch();
  const { width } = useViewport();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const myDesigns = useSelector((state) => state.myDesigns.myDesigns);
  const loading = useSelector((state) => state.myDesigns.loading);
  const [purchasedGeolocs, setPurchasedGeoloc] = React.useState([]);
  const [purchasableGeolocs, setPurchasableGeolocs] = React.useState([]);
  const myExpariences = useSelector(
    (state) => state.myExpariences.myExpariences
  );
  const exparienceLoading = useSelector(
    (state) => state.myExpariences.loadingUserExpariences
  );
  const myGroups = useSelector(
    (state) => state.user.currentUser.account.groups
  );

  const getMyExpariences = useCallback(
    async (
      resetMode = false,
      offsetParam = 0,
      fetchLimit = 10,
      isUserApi = false
    ) => {
      try {
        const thunk = await dispatch(
          fetchMyExpariences({
            offset: offsetParam,
            limit: fetchLimit,
            reset: resetMode,
            isUserApi,
          })
        );
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      } catch {
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      }
    },
    [dispatch, enqueueSnackbar, t]
  );

  const fetchDesigns = useCallback(
    async (offset = 0, limit = 10, reset = false) => {
      try {
        const thunk = await dispatch(fetchMyDesigns({ offset, limit, reset }));
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      } catch {
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      }
    },
    [dispatch, enqueueSnackbar, t]
  );

  const getPurchasableGeolocs = useCallback(
    (offset = 0, limit = 10) => {
      myExpariencesService
        .getUserPurchasableGeolocs(offset, limit)
        .then((res) => {
          if (res) {
            setPurchasableGeolocs(res);
          }
        })
        .catch((err) => {
          enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    },
    [enqueueSnackbar, t]
  );

  const getPurchasedGeolocs = useCallback(() => {
    myExpariencesService
      .getUserPurchasedGeolocs()
      .then((res) => {
        if (res) {
          setPurchasedGeoloc(res);
        }
      })
      .catch((err) => {
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    fetchDesigns(0, 10, true);
    getMyExpariences(true, 0, 10, true);
    getPurchasableGeolocs(0, 10);
    getPurchasedGeolocs();
  }, [
    fetchDesigns,
    getMyExpariences,
    getPurchasableGeolocs,
    getPurchasedGeolocs,
  ]);

  if (
    myDesigns?.length === 0 &&
    myExpariences?.length === 0 &&
    myGroups?.length === 0
  ) {
    return (
      <Typography
        variant="h6"
        sx={{
          display: "flex",
          justifyContent: "center",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {t("userDashboard.noResults")}
      </Typography>
    );
  } else
    return (
      <StyledDiv>
        <Container
          disableGutters={width < 1024}
          className={width < 1024 ? classes.generalPadding : ""}
          sx={{ marginBottom: "3rem" }}
        >
          <DashboardList
            title={t("userDashboard.geolocs")}
            redirectTo="purchased-geoloc"
            tiles={purchasedGeolocs}
          />
          <DashboardList
            title={t("userDashboard.myDesigns")}
            redirectTo="myDesigns"
            tiles={myDesigns}
            loading={loading}
          />
          <DashboardList
            title={t("userDashboard.myExpariences")}
            redirectTo="myExpariences"
            tiles={myExpariences}
            loading={exparienceLoading}
          />
          <DashboardList
            title={t("userDashboard.groups")}
            redirectTo="classes"
            tiles={myGroups}
          />
          <DashboardList
            title={t("userDashboard.boughtableGeolocs")}
            redirectTo="geolocalised-expariences"
            tiles={purchasableGeolocs.data}
          />
        </Container>
      </StyledDiv>
    );
};

export default UserDashboard;
